// utils/caseDisplayName.ts
// Generates a human-readable case identifier from request details + diagnosis
// Replaces the old nature-themed friendly names (e.g., "Ember Willow")
// with clinically useful labels (e.g., "MRI Lumbar Spine · M54.5")

/**
 * Build a display name for a case from its clinical data.
 * Falls back gracefully if fields are missing.
 *
 * Examples:
 *   "MRI Lumbar Spine · M54.5"
 *   "Humira · M06.9"
 *   "MRI Lumbar Spine" (no diagnosis)
 *   "M54.5" (no request)
 *   "Untitled Case" (nothing)
 */
export function getCaseDisplayName(
    requestDetails?: string | null,
    diagnoses?: string | null
  ): string {
    const request = cleanRequestDetails(requestDetails);
    const dx = cleanDiagnosis(diagnoses);
  
    if (request && dx) return `${request} · ${dx}`;
    if (request) return request;
    if (dx) return dx;
    return "Untitled Case";
  }
  
  /**
   * Clean up request_details for display.
   * Strips common prefixes like "Study:", "Medication:", "Procedure:" etc.
   * Truncates to a reasonable display length.
   */
  function cleanRequestDetails(raw?: string | null): string | null {
    if (!raw?.trim()) return null;
  
    let cleaned = raw.trim();
  
    // Strip common prefixes (case-insensitive)
    cleaned = cleaned.replace(
      /^(Study|Medication|Procedure|Service|Treatment|Drug|Rx|Request):\s*/i,
      ""
    );
  
    // Truncate if too long (keep first meaningful segment)
    if (cleaned.length > 50) {
      // Try to cut at a natural boundary
      const parenIdx = cleaned.indexOf("(");
      if (parenIdx > 10 && parenIdx < 50) {
        cleaned = cleaned.substring(0, parenIdx).trim();
      } else {
        cleaned = cleaned.substring(0, 47).trim() + "…";
      }
    }
  
    return cleaned || null;
  }
  
  /**
   * Extract the first ICD-10 code or short diagnosis name.
   * Prefers the code (compact), falls back to diagnosis text.
   */
  function cleanDiagnosis(raw?: string | null): string | null {
    if (!raw?.trim()) return null;
  
    const cleaned = raw.trim();
  
    // Try to extract ICD-10 code pattern (e.g., M54.5, G89.4, S83.511A)
    const icdMatch = cleaned.match(/\b([A-Z]\d{2}(?:\.\d{1,4})?[A-Z]?)\b/i);
    if (icdMatch) return icdMatch[1].toUpperCase();
  
    // If it's short enough, use as-is
    if (cleaned.length <= 30) return cleaned;
  
    // Truncate
    return cleaned.substring(0, 27).trim() + "…";
  }
  
  /**
   * Generate a friendly_name value to store in the database.
   * Call this when creating or updating a case.
   * This replaces the old generateFriendlyName() from friendlyNames.ts.
   */
  export function generateCaseDisplayName(caseData: {
    request_details?: string | null;
    diagnoses?: string | null;
  }): string {
    return getCaseDisplayName(caseData.request_details, caseData.diagnoses);
  }